import { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import {
  MODULE_PERMISSIONS,
  hasModulePermission as checkModulePermission,
  hasAnyModulePermission as checkAnyModulePermission,
  hasWritePermission as checkWritePermission,
  hasReadPermission as checkReadPermission,
  getPermissionStatus as checkPermissionStatus,
  getAvailableChannels as getChannels,
} from '../utils/modulePermissions';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user data including permissions from API
  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Fetch current user with fresh permissions from API
      const response = await authAPI.getCurrentUser();
      setUser(response.data);
    } catch (error) {
      // If token is invalid or expired, clear it
      console.error('Failed to fetch user data:', error);
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, user: userData } = response.data;

      // Only store token in localStorage, not user data
      localStorage.setItem('token', token);

      // Fetch full user data with permissions from API
      const userResponse = await authAPI.getCurrentUser();
      setUser(userResponse.data);

      return response;
    } catch (error) {
      // Re-throw the error so it can be caught in the Login component
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  // Refresh user data (useful when permissions might have changed)
  const refreshUser = async () => {
    await fetchUserData();
  };

  const hasRole = (...roles) => {
    return user && roles.includes(user.role);
  };

  const isAdmin = () => hasRole('ADMIN');
  const isStaff = () => hasRole('ADMIN', 'STAFF');

  // Module permission helpers
  const hasModulePermission = (module) => checkModulePermission(user, module);
  const hasAnyModulePermission = (modules) => checkAnyModulePermission(user, modules);
  const getAvailableChannels = () => getChannels(user);

  // RBAC permission helpers
  const hasWritePermission = (module) => checkWritePermission(user, module);
  const hasReadPermission = (module) => checkReadPermission(user, module);
  const getPermissionStatus = (module) => checkPermissionStatus(user, module);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        refreshUser,
        hasRole,
        isAdmin,
        isStaff,
        isAuthenticated: !!user,
        // Module permission methods
        hasModulePermission,
        hasAnyModulePermission,
        getAvailableChannels,
        // RBAC permission methods
        hasWritePermission,
        hasReadPermission,
        getPermissionStatus,
        MODULE_PERMISSIONS,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
